import {createSlice, PayloadAction} from '@reduxjs/toolkit';

import {PageComment} from '~shared/models/wiki';

import {getComments, deleteComment, addComment, editComment, getThreadComments} from './thunks';

export interface CommentsState {
    entities: Record<PageComment['id'], PageComment>;
    threads: Record<
        PageComment['id'],
        {
            info: {total: number};
            comments: number[];
            loading: boolean;
        }
    >;
    isLoading?: boolean;
    selectedId?: number;
    pageId?: number;
}

const initialState: CommentsState = {
    isLoading: true,
    entities: {},
    threads: {},
};

interface SetLoadingPayload {
    isLoading: boolean;
}

interface SetHtmlPayload {
    html: string;
    commentId: number;
}

interface ResetPayload {
    pageId: number;
}

interface SelectCommentPayload {
    commentId: number;
}

const setCommentDetails = (state: CommentsState, comment: PageComment) => {
    state.entities[comment.id] = Object.assign({}, state.entities[comment.id], comment);
};

const setThreadInfo = (state: CommentsState, comment: PageComment) => {
    if (comment.thread_info) {
        const info = comment.thread_info;
        setCommentDetails(state, info.last_comment);
        state.threads[comment.id] = {
            info: {total: info.total_posts},
            comments: [info.last_comment.id],
            loading: false,
        };
    }
};

const {reducer, actions} = createSlice({
    name: 'comments',
    initialState,
    reducers: {
        setHtml(state, {payload: {commentId, html}}: PayloadAction<SetHtmlPayload>) {
            const entity = state.entities[commentId];
            if (entity) {
                entity.html = html;
            }
        },
        reset(_state, _action: PayloadAction<ResetPayload>) {
            return initialState;
        },
        setLoading(state, {payload: {isLoading}}: PayloadAction<SetLoadingPayload>) {
            state.isLoading = isLoading;
        },
        selectComment(state, {payload: {commentId}}: PayloadAction<SelectCommentPayload>) {
            state.selectedId = commentId;
        },
    },
    extraReducers: (builder) =>
        builder
            .addCase(getComments.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(getComments.fulfilled, (state, {payload}) => {
                const {
                    comments: {results},
                } = payload;

                results.forEach((comment) => {
                    setCommentDetails(state, comment);
                    setThreadInfo(state, comment);
                });
                state.isLoading = false;
            })
            .addCase(getComments.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(deleteComment.fulfilled, (state, {payload}) => {
                state.isLoading = false;
                const comment = state.entities[payload.comment.id];
                if (!comment) return;

                comment.is_deleted = true;
                if (comment.thread_id) {
                    const thread = state.threads[comment.thread_id];
                    if (!thread) return;

                    thread.info.total--;
                    const index = thread.comments.findIndex((id) => id === comment.id);
                    if (index !== -1) {
                        thread.comments.splice(index, 1);
                    }
                }
            })
            .addCase(deleteComment.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(addComment.fulfilled, (state, {payload: {comment}}) => {
                setCommentDetails(state, comment);
                state.selectedId = comment.id;
                state.isLoading = false;

                if (comment.thread_info) {
                    setCommentDetails(state, comment.thread_info.last_comment);
                    setThreadInfo(state, comment);
                }

                if (comment.thread_id) {
                    let thread = state.threads[comment.thread_id];
                    if (!thread) {
                        thread = state.threads[comment.thread_id] = {
                            info: {total: 0},
                            comments: [],
                            loading: false,
                        };
                    }

                    thread.comments.push(comment.id);
                    thread.info.total++;
                }
            })
            .addCase(addComment.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(editComment.fulfilled, (state, {payload}) => {
                const entity = state.entities[payload.id];

                if (!entity) return;

                entity.html = '';
                setCommentDetails(state, payload);
                state.isLoading = false;
            })
            .addCase(editComment.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(getThreadComments.pending, (state, {meta}) => {
                const {commentId} = meta.arg;
                const thread = state.threads[commentId];
                if (thread) {
                    thread.loading = true;
                }
            })
            .addCase(getThreadComments.fulfilled, (state, {payload}) => {
                const {
                    commentId,
                    comments: {results},
                } = payload;

                const ids: number[] = [];
                for (const comment of results) {
                    setCommentDetails(state, comment);
                    ids.push(comment.id);
                }

                state.threads[commentId] = {
                    info: {total: ids.length},
                    comments: ids,
                    loading: false,
                };
            }),
});

export {actions, reducer};
